import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { Modal, Spin, notification, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import {
  PlusOutlined,
  SearchOutlined,
  CloseOutlined,
  AppstoreOutlined,
  FilterOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import { apiRequest } from "util/services";
import styles from "./kpiView.module.scss";
import {
  buildEntriesFromMetricList,
  filterBuilderTopicsBySource,
  formatTopicReviewCount24h,
  groupTopicsByLevel0,
  mergeMetricListLabels,
  sortSourcesByLastUpdate,
  formatSourceLastUpdate,
  topicSelectionKey,
} from "./kpiViewUtils";
import { kpiQuotaCreateDisabledTooltip } from "./KpiQuotaFooter";
import { MOBILE_BUILDER_BREAKPOINT } from "./kpiBuilderCoachMarkUtils";
import {
  loadKpiBuilderCollapsed,
  saveKpiBuilderCollapsed,
} from "./kpiBuilderPanelStorage";

const INITIAL_VISIBLE = 2;

function QuotaGatedButton({ disabled, title, className, onClick, children, ...rest }) {
  const button = (
    <button
      type="button"
      className={className}
      disabled={disabled}
      onClick={onClick}
      {...rest}
    >
      {children}
    </button>
  );
  if (!disabled || !title) return button;
  return (
    <Tooltip title={title}>
      <span className={styles.quotaGatedWrap}>{button}</span>
    </Tooltip>
  );
}

function normalizeDashboardPivots(raw) {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof raw === "object" ? raw : null;
}

function collectPivotValuesFromBucket(bucket) {
  const values = new Set();
  if (Array.isArray(bucket)) {
    bucket.forEach((val) => {
      if (val != null && String(val).trim()) values.add(String(val));
    });
    return values;
  }
  if (bucket && typeof bucket === "object") {
    Object.entries(bucket).forEach(([val, cnt]) => {
      if (val == null || !String(val).trim()) return;
      if (cnt == null || Number(cnt) > 0) values.add(String(val));
    });
  }
  return values;
}

function extractPivotKeysFromResponse(successPayload) {
  if (!Array.isArray(successPayload)) return [];
  const keys = new Set();
  successPayload.forEach((item) => {
    Object.entries(item || {}).forEach(([, rawPivots]) => {
      const dashboardPivots = normalizeDashboardPivots(rawPivots);
      Object.keys(dashboardPivots || {}).forEach((key) => keys.add(key));
    });
  });
  return Array.from(keys).sort((a, b) => a.localeCompare(b));
}

function extractPivotValuesFromResponse(successPayload, pivotKey) {
  if (!Array.isArray(successPayload) || !pivotKey) return [];
  const values = new Set();
  successPayload.forEach((item) => {
    Object.entries(item || {}).forEach(([, rawPivots]) => {
      const dashboardPivots = normalizeDashboardPivots(rawPivots);
      collectPivotValuesFromBucket(dashboardPivots?.[pivotKey]).forEach((val) =>
        values.add(val)
      );
    });
  });
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}

export default function KpiBuilderPanel({
  sources = [],
  sourcesLoading = false,
  creating = false,
  kpiLimitReached = false,
  onCreateKpi,
  onBuilderEngagement,
  onOpenAdvisor,
  showAdvisorButton = false,
  forceExpanded = false,
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(() => loadKpiBuilderCollapsed(true));
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= MOBILE_BUILDER_BREAKPOINT
  );
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [topicEntries, setTopicEntries] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicCountsLoading, setTopicCountsLoading] = useState(false);
  const [metricListPayload, setMetricListPayload] = useState(null);
  const [selectedTopicIds, setSelectedTopicIds] = useState(() => new Set());
  const [pivots, setPivots] = useState([]);
  const [query, setQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState(() => new Set());
  const [dashboardPickerOpen, setDashboardPickerOpen] = useState(false);
  const [dashboardQuery, setDashboardQuery] = useState("");
  const [pivotFormOpen, setPivotFormOpen] = useState(false);
  const [pivotData, setPivotData] = useState([]);
  const [pivotKeys, setPivotKeys] = useState([]);
  const [pivotsLoading, setPivotsLoading] = useState(false);
  const [pivotDraft, setPivotDraft] = useState({
    key: "",
    value: "",
  });
  const [pivotValuePickerOpen, setPivotValuePickerOpen] = useState(false);
  const [pivotValueQuery, setPivotValueQuery] = useState("");
  const [createChoiceOpen, setCreateChoiceOpen] = useState(false);
  const dashboardPickerRef = useRef(null);
  const dashboardSearchRef = useRef(null);
  const pivotValuePickerRef = useRef(null);
  const pivotValueSearchRef = useRef(null);

  const notifyBuilderEngagement = useCallback(() => {
    if (onBuilderEngagement) {
      onBuilderEngagement();
    }
  }, [onBuilderEngagement]);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BUILDER_BREAKPOINT}px)`);
    const onChange = () => setIsMobileLayout(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const expandPanel = useCallback(() => {
    setCollapsed(false);
    saveKpiBuilderCollapsed(false);
    notifyBuilderEngagement();
  }, [notifyBuilderEngagement]);

  const collapsePanel = useCallback(() => {
    setCollapsed(true);
    saveKpiBuilderCollapsed(true);
  }, []);

  const showCollapsedRail = collapsed && !forceExpanded && !isMobileLayout;

  const entryMap = useMemo(() => {
    const map = new Map();
    topicEntries.forEach((entry) => {
      const key = entry.topicKey || topicSelectionKey(entry);
      if (key) map.set(key, entry);
    });
    return map;
  }, [topicEntries]);

  const availableDashboards = useMemo(() => {
    const unselected = sources.filter(
      (s) => !selectedDashboards.some((d) => String(d.id) === String(s.id))
    );
    return sortSourcesByLastUpdate(unselected);
  }, [sources, selectedDashboards]);

  const filteredAvailableDashboards = useMemo(() => {
    const q = dashboardQuery.trim().toLowerCase();
    const list = !q
      ? availableDashboards
      : availableDashboards.filter((source) =>
          String(source?.Name ?? "")
            .toLowerCase()
            .includes(q)
        );
    return sortSourcesByLastUpdate(list);
  }, [availableDashboards, dashboardQuery]);

  useEffect(() => {
    if (selectedDashboards.length === 0) {
      setTopicEntries([]);
      setMetricListPayload(null);
      setTopicsLoading(false);
      setTopicCountsLoading(false);
      return;
    }

    let cancelled = false;
    const dashboardIds = selectedDashboards.map((d) => parseInt(d.id, 10));

    const loadTopics = async () => {
      setTopicsLoading(true);
      setTopicCountsLoading(false);
      const result = await apiRequest(
        "executive_summary/v2/metricList",
        "POST",
        {
          dashboard_ids: dashboardIds,
          block_location: "Top",
        },
        true
      );

      if (cancelled) return;

      if (result?.success) {
        const labels = mergeMetricListLabels(result.success);
        setMetricListPayload(result.success);
        setTopicEntries(
          buildEntriesFromMetricList(result.success, selectedDashboards)
        );
        setTopicCountsLoading(labels.length > 0);
      } else {
        setMetricListPayload(null);
        setTopicEntries([]);
        notification.error({
          message: result?.error || t("team_kpi_view.fetch_topics_failed", "Could not load topics"),
        });
      }
      setTopicsLoading(false);
    };

    loadTopics();
    return () => {
      cancelled = true;
    };
  }, [selectedDashboards]);

  useEffect(() => {
    if (!metricListPayload || selectedDashboards.length === 0) {
      setTopicCountsLoading(false);
      return;
    }

    let cancelled = false;
    const dashboardIds = selectedDashboards.map((d) => parseInt(d.id, 10));
    const topicLabels = mergeMetricListLabels(metricListPayload);

    if (topicLabels.length === 0) {
      setTopicCountsLoading(false);
      setTopicEntries((prev) =>
        prev.map((entry) => ({ ...entry, reviewCount24h: 0 }))
      );
      return;
    }

    const loadReviewCounts = async () => {
      setTopicCountsLoading(true);
      const result = await apiRequest(
        "executive_summary/v2/topic_review_counts",
        "POST",
        {
          dashboard_ids: dashboardIds,
          topic_labels: topicLabels,
          hours: 24,
        },
        true
      );

      if (cancelled) return;

      const counts = result?.success && typeof result.success === "object"
        ? result.success
        : {};
      setTopicEntries(
        buildEntriesFromMetricList(metricListPayload, selectedDashboards, counts)
      );
      if (!result?.success && result?.error) {
        notification.error({
          message: result.error || t("team_kpi_view.fetch_topic_counts_failed", "Could not load topic review counts"),
        });
      }
      setTopicCountsLoading(false);
    };

    loadReviewCounts();
    return () => {
      cancelled = true;
    };
  }, [metricListPayload, selectedDashboards]);

  useEffect(() => {
    if (selectedDashboards.length === 0) {
      setPivotData([]);
      setPivotKeys([]);
      setPivotsLoading(false);
      setPivots([]);
      setPivotDraft({ key: "", value: "" });
      return;
    }

    let cancelled = false;
    const dashboardIds = selectedDashboards.map((d) => parseInt(d.id, 10));

    const loadPivots = async () => {
      setPivotsLoading(true);
      const result = await apiRequest(
        "report/pivots",
        "POST",
        { dashboard_ids: dashboardIds },
        true
      );

      if (cancelled) return;

      if (result?.success) {
        const data = result.success;
        setPivotData(data);
        const keys = extractPivotKeysFromResponse(data);
        setPivotKeys(keys);
        setPivotDraft((prev) => {
          const nextKey =
            prev.key && keys.includes(prev.key) ? prev.key : keys[0] || "";
          return { key: nextKey, value: "" };
        });
        setPivots((prev) =>
          prev.filter((p) => keys.includes(p.key))
        );
      } else {
        setPivotData([]);
        setPivotKeys([]);
        setPivotDraft({ key: "", value: "" });
        notification.error({
          message: result?.error || t("team_kpi_view.fetch_pivots_failed", "Could not load pivots"),
        });
      }
      setPivotsLoading(false);
    };

    loadPivots();
    return () => {
      cancelled = true;
    };
  }, [selectedDashboards]);

  useEffect(() => {
    if (selectedDashboards.length > 0 && pivotKeys.length > 0) {
      setPivotFormOpen(true);
    } else if (selectedDashboards.length === 0) {
      setPivotFormOpen(false);
    }
  }, [selectedDashboards.length, pivotKeys.length]);

  const filteredTopics = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = filterBuilderTopicsBySource(
      topicEntries,
      selectedDashboards,
      "all"
    );

    if (!q) return list;
    return list.filter(
      (entry) =>
        entry.childName?.toLowerCase().includes(q) ||
        entry.label.toLowerCase().includes(q) ||
        entry.category.toLowerCase().includes(q)
    );
  }, [topicEntries, query, selectedDashboards]);

  const groupedTopics = useMemo(
    () => groupTopicsByLevel0(filteredTopics),
    [filteredTopics]
  );

  const topicsEmptyMessage = useMemo(() => {
    if (selectedDashboards.length === 0) {
      return t("team_kpi_view.builder_select_dashboard", "Select a dashboard");
    }
    if (selectedDashboards.length > 1 && topicEntries.length === 0) {
      return t("team_kpi_view.builder_no_common_topics", "No common topics found");
    }
    return t("team_kpi_view.builder_no_topics", "No topics found");
  }, [selectedDashboards, topicEntries.length, t]);

  useEffect(() => {
    const validKeys = new Set(
      topicEntries.map((entry) => entry.topicKey || topicSelectionKey(entry))
    );
    setSelectedTopicIds((prev) => {
      const next = new Set([...prev].filter((key) => validKeys.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [topicEntries]);

  const selectedTopicCount = selectedTopicIds.size;
  const selectedDashboardCount = selectedDashboards.length;
  const pivotsForSubmit = useMemo(() => {
    const list = [...pivots];
    const draftKey = String(pivotDraft.key ?? "").trim();
    const draftValue = String(pivotDraft.value ?? "").trim();
    if (draftKey && draftValue) {
      const exists = list.some(
        (p) => p.key === draftKey && String(p.value) === draftValue
      );
      if (!exists) list.push({ key: draftKey, value: draftValue });
    }
    return list;
  }, [pivots, pivotDraft.key, pivotDraft.value]);
  const isValid =
    selectedDashboardCount >= 1 &&
    (selectedTopicCount >= 1 || pivotsForSubmit.length >= 1);

  const quotaDisabledTooltip = kpiQuotaCreateDisabledTooltip(kpiLimitReached, t);

  const summary = useMemo(() => {
    if (kpiLimitReached) {
      return {
        ok: false,
        message: t("team_kpi_view.98", "Your KPI quota is full"),
      };
    }
    if (!isValid) {
      if (selectedDashboardCount === 0) {
        return {
          ok: false,
          message: t("team_kpi_view.builder_need_dashboard", "Select at least 1 dashboard"),
        };
      }
      return {
        ok: false,
        message: t(
          "team_kpi_view.builder_need_topic_or_pivot",
          "Select at least 1 topic or pivot",
        ),
      };
    }
    if (selectedTopicCount > 0) {
      return {
        ok: true,
        message: t("team_kpi_view.builder_summary_ok", {
          topics: selectedTopicCount,
          dashboards: selectedDashboardCount,
          pivots: pivotsForSubmit.length,
          defaultValue: "{{topics}} topic · {{dashboards}} dashboard · {{pivots}} pivot",
        }),
      };
    }
    return {
      ok: true,
      message: t("team_kpi_view.builder_summary_ok_no_topics", {
        dashboards: selectedDashboardCount,
        pivots: pivotsForSubmit.length,
        defaultValue: "{{dashboards}} dashboard · {{pivots}} pivot",
      }),
    };
  }, [
    kpiLimitReached,
    t,
    isValid,
    selectedTopicCount,
    selectedDashboardCount,
    pivotsForSubmit.length,
  ]);

  const pivotValueOptions = useMemo(
    () => extractPivotValuesFromResponse(pivotData, pivotDraft.key),
    [pivotData, pivotDraft.key]
  );

  const filteredPivotValueOptions = useMemo(() => {
    const q = pivotValueQuery.trim().toLowerCase();
    if (!q) return pivotValueOptions;
    return pivotValueOptions.filter((val) =>
      String(val).toLowerCase().includes(q)
    );
  }, [pivotValueOptions, pivotValueQuery]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        dashboardPickerRef.current &&
        !dashboardPickerRef.current.contains(e.target)
      ) {
        setDashboardPickerOpen(false);
        setDashboardQuery("");
      }
      if (
        pivotValuePickerRef.current &&
        !pivotValuePickerRef.current.contains(e.target)
      ) {
        setPivotValuePickerOpen(false);
        setPivotValueQuery("");
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    if (dashboardPickerOpen) {
      const timer = window.setTimeout(() => {
        if (dashboardSearchRef.current) {
          dashboardSearchRef.current.focus();
        }
      }, 0);
      return () => window.clearTimeout(timer);
    }
    setDashboardQuery("");
    return undefined;
  }, [dashboardPickerOpen]);

  useEffect(() => {
    if (
      pivotDraft.value &&
      !pivotValueOptions.includes(pivotDraft.value)
    ) {
      setPivotDraft((prev) => ({ ...prev, value: "" }));
    }
  }, [pivotDraft.value, pivotValueOptions]);

  useEffect(() => {
    setPivotValuePickerOpen(false);
    setPivotValueQuery("");
  }, [pivotDraft.key]);

  useEffect(() => {
    if (pivotValuePickerOpen) {
      const timer = window.setTimeout(() => {
        if (pivotValueSearchRef.current) {
          pivotValueSearchRef.current.focus();
        }
      }, 0);
      return () => window.clearTimeout(timer);
    }
    setPivotValueQuery("");
    return undefined;
  }, [pivotValuePickerOpen]);

  const toggleTopic = (topicKey) => {
    notifyBuilderEngagement();
    setSelectedTopicIds((prev) => {
      const next = new Set(prev);
      if (next.has(topicKey)) next.delete(topicKey);
      else next.add(topicKey);
      return next;
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const addDashboard = (source) => {
    notifyBuilderEngagement();
    setSelectedDashboards((prev) => [
      ...prev,
      { id: source.id, name: source.Name },
    ]);
    setDashboardPickerOpen(false);
    setDashboardQuery("");
  };

  const toggleDashboardPicker = () => {
    setDashboardPickerOpen((open) => {
      if (open) setDashboardQuery("");
      return !open;
    });
  };

  const removeDashboard = (id) => {
    setSelectedDashboards((prev) =>
      prev.filter((d) => String(d.id) !== String(id))
    );
  };

  const appendPivotChip = (prev, key, value) => {
    const nextKey = String(key ?? "").trim();
    const nextValue = String(value ?? "").trim();
    if (!nextKey || !nextValue) return prev;
    if (prev.some((p) => p.key === nextKey && String(p.value) === nextValue)) {
      return prev;
    }
    return [...prev, { key: nextKey, value: nextValue }];
  };

  const selectPivotValue = (value) => {
    const key = String(pivotDraft.key ?? "").trim();
    const nextValue = String(value ?? "").trim();
    setPivotValuePickerOpen(false);
    setPivotValueQuery("");
    if (key && nextValue) {
      notifyBuilderEngagement();
      setPivots((prev) => appendPivotChip(prev, key, nextValue));
      setPivotDraft({ key, value: "" });
    }
  };

  const togglePivotValuePicker = () => {
    if (pivotsLoading || pivotValueOptions.length === 0) return;
    setPivotValuePickerOpen((open) => {
      if (open) setPivotValueQuery("");
      return !open;
    });
  };

  const addPivot = () => {
    const key = String(pivotDraft.key ?? "").trim();
    const value = String(pivotDraft.value ?? "").trim();
    if (!key || !value) return;
    notifyBuilderEngagement();
    setPivots((prev) => appendPivotChip(prev, key, value));
    setPivotDraft({ key, value: "" });
    setPivotValuePickerOpen(false);
    setPivotValueQuery("");
    setPivotFormOpen(false);
  };

  const removePivot = (index) => {
    setPivots((prev) => prev.filter((_, i) => i !== index));
  };

  const resetBuilderForm = () => {
    setSelectedTopicIds(new Set());
    setSelectedDashboards([]);
    setPivots([]);
    setQuery("");
    setExpandedCategories(new Set());
    setPivotFormOpen(false);
    setCreateChoiceOpen(false);
  };

  const submitCreate = async (mode) => {
    if (!isValid || !onCreateKpi) return;
    notifyBuilderEngagement();
    setCreateChoiceOpen(false);
    const topics = Array.from(selectedTopicIds)
      .map((key) => entryMap.get(key)?.label || key)
      .filter(Boolean);
    const dashboards = selectedDashboards.map((d) => d.id);
    const effectivePivots = pivotsForSubmit;
    const ok = await onCreateKpi({ topics, dashboards, pivots: effectivePivots, mode });
    if (ok) {
      resetBuilderForm();
      collapsePanel();
    }
  };

  const handleCreate = () => {
    if (!isValid || !onCreateKpi) return;
    notifyBuilderEngagement();
    if (selectedTopicCount > 1) {
      setCreateChoiceOpen(true);
      return;
    }
    submitCreate("combined");
  };

  return (
    <>
      {showCollapsedRail ? (
        <aside
          className={`${styles.kpiBuilder} ${styles.kpiBuilderRail}`}
          id="kpi-builder-panel"
        >
          <button
            type="button"
            className={styles.kpiBuilderRailPrimaryBtn}
            onClick={expandPanel}
            aria-label={t("team_kpi_view.builder_expand", "Open KPI builder")}
            title={t("team_kpi_view.builder_expand", "Open KPI builder")}
          >
            <PlusOutlined aria-hidden />
          </button>
          <span className={styles.kpiBuilderRailLabel} aria-hidden>
            {t("team_kpi_view.builder_rail_label", "Create KPI")}
          </span>
          <div className={styles.kpiBuilderRailFooter}>
            <button
              type="button"
              className={styles.kpiBuilderRailExpandBtn}
              onClick={expandPanel}
              aria-label={t("team_kpi_view.builder_expand", "Open KPI builder")}
              title={t("team_kpi_view.builder_expand", "Open KPI builder")}
            >
              <MenuUnfoldOutlined aria-hidden />
            </button>
          </div>
        </aside>
      ) : (
    <aside className={styles.kpiBuilder} id="kpi-builder-panel">
      <div className={styles.builderHeader}>
        <div className={styles.builderHeaderTop}>
          <h3 className={styles.builderTitle}>
            {t("team_kpi_view.builder_title", "Create KPI")}
          </h3>
          <div className={styles.builderHeaderActions}>
            {showAdvisorButton && onOpenAdvisor ? (
              <button
                type="button"
                className={styles.advisorCreateBtn}
                onClick={onOpenAdvisor}
              >
                {t("team_kpi_view.advisorKpiCreate", "Create KPI with Advisor")}
              </button>
            ) : null}
            {!isMobileLayout ? (
              <button
                type="button"
                className={styles.kpiBuilderCollapseBtn}
                onClick={collapsePanel}
                aria-label={t("team_kpi_view.builder_collapse", "Close KPI builder")}
                title={t("team_kpi_view.builder_collapse", "Close KPI builder")}
              >
                <MenuFoldOutlined aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
        <p className={styles.builderSubtitle}>
          {t("team_kpi_view.builder_subtitle", "Dashboard required · Topic or Pivot is enough")}
        </p>
      </div>

      <div className={styles.builderBody}>
        {/* Dashboard */}
        <section
          className={`${styles.builderSection} ${
            dashboardPickerOpen ? styles.builderSectionRaised : ""
          }`}
        >
          <div className={styles.builderDropdownAnchor} ref={dashboardPickerRef}>
            <div className={styles.builderSectionHead}>
              <span className={styles.builderSectionLabel}>
                Dashboard <span className={styles.requiredMark}>*</span>
              </span>
              <div className={styles.builderSectionAction}>
                <QuotaGatedButton
                  className={styles.textActionBtn}
                  onClick={toggleDashboardPicker}
                  disabled={
                    kpiLimitReached
                    || (availableDashboards.length === 0 && !sourcesLoading)
                  }
                  title={quotaDisabledTooltip}
                >
                  <PlusOutlined /> {t("team_kpi_view.builder_add", "Add")}
                </QuotaGatedButton>
              </div>
            </div>
            {dashboardPickerOpen && availableDashboards.length > 0 && (
              <div className={styles.builderDropdown}>
                <div className={styles.builderDropdownSearch}>
                  <SearchOutlined className={styles.builderDropdownSearchIcon} />
                  <input
                    ref={dashboardSearchRef}
                    type="search"
                    className={styles.builderDropdownSearchInput}
                    placeholder={t("team_kpi_view.builder_search_dashboard", "Search dashboards...")}
                    value={dashboardQuery}
                    onChange={(e) => setDashboardQuery(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>
                <div className={styles.builderDropdownList}>
                  {filteredAvailableDashboards.length > 0 ? (
                    filteredAvailableDashboards.map((source) => (
                      <button
                        key={source.id}
                        type="button"
                        className={styles.builderDropdownItem}
                        onClick={() => addDashboard(source)}
                      >
                        <span className={styles.builderDropdownItemName}>
                          {source.Name}
                        </span>
                        {formatSourceLastUpdate(source) && (
                          <span className={styles.builderDropdownItemMeta}>
                            {formatSourceLastUpdate(source)}
                          </span>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className={styles.builderDropdownEmpty}>
                      {t("team_kpi_view.builder_no_results", "No results")}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className={styles.chipRow}>
            {selectedDashboards.map((d) => (
              <span key={d.id} className={styles.dashboardChip}>
                <AppstoreOutlined className={styles.chipIcon} />
                <span className={styles.chipLabel}>{d.name}</span>
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() => removeDashboard(d.id)}
                  aria-label={t("team_kpi_view.remove", "Remove")}
                >
                  <CloseOutlined />
                </button>
              </span>
            ))}
          </div>
        </section>

        {/* Pivot — dashboard sonrası; topic seçmeden kullanılabilir */}
        <section
          className={`${styles.builderSection} ${styles.builderSectionPivot} ${
            pivotFormOpen || pivotValuePickerOpen ? styles.builderSectionRaised : ""
          }`}
        >
          <div className={styles.builderSectionHead}>
            <span className={styles.builderSectionLabel}>
              {t("team_kpi_view.builder_pivot", "Pivot")}{" "}
              <span className={styles.optionalHint}>
                {t("team_kpi_view.builder_optional", "(optional)")}
              </span>
            </span>
            <QuotaGatedButton
              className={styles.textActionBtn}
              onClick={() => setPivotFormOpen(true)}
              disabled={
                kpiLimitReached
                || selectedDashboards.length === 0
                || pivotKeys.length === 0
              }
              title={quotaDisabledTooltip}
            >
              <PlusOutlined /> {t("team_kpi_view.builder_add", "Add")}
            </QuotaGatedButton>
          </div>

          {pivotFormOpen && (
            <div className={styles.pivotInlineForm}>
              <div className={styles.pivotInlineHead}>
                <span className={styles.pivotInlineTitle}>
                  {t("team_kpi_view.builder_pivot_add", "Add pivot")}
                </span>
                <button
                  type="button"
                  className={styles.pivotInlineDismiss}
                  onClick={() => setPivotFormOpen(false)}
                  aria-label={t("common.close", "Close")}
                >
                  <CloseOutlined />
                </button>
              </div>
              <Spin spinning={pivotsLoading}>
                <label className={styles.pivotInlineField}>
                  <span>{t("team_kpi_view.builder_pivot_key", "Pivot key")}</span>
                  <select
                    value={pivotDraft.key}
                    onChange={(e) =>
                      setPivotDraft({ key: e.target.value, value: "" })
                    }
                    disabled={pivotKeys.length === 0}
                  >
                    {pivotKeys.length === 0 ? (
                      <option value="">
                        {t("team_kpi_view.builder_pivot_none", "No pivots")}
                      </option>
                    ) : (
                      pivotKeys.map((key) => (
                        <option key={key} value={key}>
                          {key}
                        </option>
                      ))
                    )}
                  </select>
                </label>
                <div className={styles.pivotInlineField}>
                  <span>{t("team_kpi_view.builder_pivot_value", "Pivot value")}</span>
                  <div
                    className={styles.pivotValuePicker}
                    ref={pivotValuePickerRef}
                  >
                    <button
                      type="button"
                      className={styles.pivotValueTrigger}
                      onClick={togglePivotValuePicker}
                      disabled={
                        pivotsLoading || pivotValueOptions.length === 0
                      }
                    >
                      <span className={styles.pivotValueTriggerLabel}>
                        {pivotDraft.value ||
                          (pivotsLoading
                            ? t("team_kpi_view.builder_loading", "Loading...")
                            : pivotValueOptions.length > 0
                            ? t("team_kpi_view.builder_pivot_select", {
                                count: pivotValueOptions.length,
                                defaultValue: "Select ({{count}})",
                              })
                            : t("team_kpi_view.builder_pivot_no_value", "No values"))}
                      </span>
                    </button>
                    {pivotValuePickerOpen && (
                      <div className={styles.pivotValueDropdown}>
                        <div className={styles.builderDropdownSearch}>
                          <SearchOutlined
                            className={styles.builderDropdownSearchIcon}
                          />
                          <input
                            ref={pivotValueSearchRef}
                            type="search"
                            className={styles.builderDropdownSearchInput}
                            placeholder={t(
                              "team_kpi_view.builder_search_pivot_value",
                              "Search pivot values...",
                            )}
                            value={pivotValueQuery}
                            onChange={(e) => setPivotValueQuery(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className={styles.builderDropdownList}>
                          {filteredPivotValueOptions.length > 0 ? (
                            filteredPivotValueOptions.map((val) => (
                              <button
                                key={val}
                                type="button"
                                className={`${styles.builderDropdownItem} ${
                                  pivotDraft.value === val
                                    ? styles.builderDropdownItemActive
                                    : ""
                                }`}
                                onClick={() => selectPivotValue(val)}
                              >
                                {val}
                              </button>
                            ))
                          ) : (
                            <div className={styles.builderDropdownEmpty}>
                              {t("team_kpi_view.builder_no_results", "No results")}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.pivotInlineSubmit}
                  onClick={addPivot}
                  disabled={!pivotDraft.key || !pivotDraft.value}
                >
                  {t("team_kpi_view.builder_add", "Add")}
                </button>
              </Spin>
            </div>
          )}

          <div className={styles.chipRow}>
            {pivots.map((pivot, index) => (
              <span key={`${pivot.key}-${pivot.value}-${index}`} className={styles.pivotChip}>
                <FilterOutlined className={styles.chipIcon} />
                <span className={styles.chipLabel}>
                  {pivot.key} = {pivot.value}
                </span>
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() => removePivot(index)}
                  aria-label={t("team_kpi_view.remove", "Remove")}
                >
                  <CloseOutlined />
                </button>
              </span>
            ))}
          </div>
        </section>

        {/* Topics */}
        <section
          className={`${styles.builderSection} ${styles.builderSectionTopics} ${
            selectedDashboards.length === 0 ? styles.builderSectionTopicsIdle : ""
          }`}
        >
          <div className={styles.builderSectionHead}>
            <span className={styles.builderSectionLabel}>
              {t("team_kpi_view.builder_topics", "Topics")}{" "}
              <span className={styles.optionalHint}>
                {t("team_kpi_view.builder_optional", "(optional)")}
              </span>
            </span>
            {selectedTopicCount > 0 && (
              <span className={styles.selectedCount}>
                {t("team_kpi_view.builder_selected_count", {
                  count: selectedTopicCount,
                  defaultValue: "{{count}} selected",
                })}
              </span>
            )}
          </div>

          <div className={styles.topicSearchWrap}>
            <SearchOutlined className={styles.topicSearchIcon} />
            <input
              type="search"
              className={styles.topicSearchInput}
              placeholder={t("team_kpi_view.builder_search_topic", "Search topics...")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className={styles.topicsList}>
            {selectedDashboards.length === 0 ? (
              <div className={styles.builderTopicsPlaceholder}>
                <AppstoreOutlined className={styles.builderTopicsPlaceholderIcon} />
                <p className={styles.builderTopicsPlaceholderTitle}>
                  {t(
                    "team_kpi_view.builder_topics_placeholder_title",
                    "Select a dashboard to continue",
                  )}
                </p>
                <p className={styles.builderTopicsPlaceholderHint}>
                  {t(
                    "team_kpi_view.builder_topics_placeholder_hint",
                    "After selecting a dashboard you can add topic and pivot filters.",
                  )}
                </p>
              </div>
            ) : (
            <Spin spinning={sourcesLoading || topicsLoading || topicCountsLoading}>
              {groupedTopics.map(({ category, items }) => {
                const isExpanded = expandedCategories.has(category);
                const visibleItems = isExpanded
                  ? items
                  : items.slice(0, INITIAL_VISIBLE);
                const hiddenCount = Math.max(0, items.length - INITIAL_VISIBLE);

                return (
                  <div key={category} className={styles.catalogCategory}>
                    <div className={styles.catalogCategoryLabel}>
                      {category}
                    </div>
                    {visibleItems.map((entry) => {
                      const topicKey =
                        entry.topicKey || topicSelectionKey(entry);
                      const checked = selectedTopicIds.has(topicKey);
                      return (
                        <label
                          key={topicKey}
                          className={`${styles.topicRow} ${
                            checked ? styles.topicRowSelected : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            className={styles.topicCheckbox}
                            checked={checked}
                            onChange={() => toggleTopic(topicKey)}
                          />
                          <span className={styles.topicRowBody}>
                            <span className={styles.catalogItemName}>
                              {entry.childName || entry.label}
                            </span>
                            <span className={styles.catalogItemMeta}>
                              {formatTopicReviewCount24h(entry.reviewCount24h, {
                                loading:
                                  topicCountsLoading && entry.reviewCount24h == null,
                                t,
                              })}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                    {!isExpanded && hiddenCount > 0 && (
                      <button
                        type="button"
                        className={styles.catalogLoadMore}
                        onClick={() => toggleCategory(category)}
                      >
                        {t("team_kpi_view.builder_show_more", {
                          count: hiddenCount,
                          defaultValue: "Show more ({{count}})",
                        })}
                      </button>
                    )}
                    {isExpanded && hiddenCount > 0 && (
                      <button
                        type="button"
                        className={styles.catalogLoadMore}
                        onClick={() => toggleCategory(category)}
                      >
                        {t("team_kpi_view.builder_show_less", "Show less")}
                      </button>
                    )}
                  </div>
                );
              })}
              {!sourcesLoading && !topicsLoading && groupedTopics.length === 0 && (
                <div className={styles.catalogEmpty}>{topicsEmptyMessage}</div>
              )}
            </Spin>
            )}
          </div>
        </section>
      </div>

      <div className={styles.builderFooter}>
        <div
          className={`${styles.builderSummary} ${
            summary.ok ? styles.builderSummaryOk : styles.builderSummaryError
          }`}
        >
          <span className={styles.summaryDot} />
          <span>{summary.message}</span>
        </div>
        <button
          type="button"
          className={styles.builderCta}
          disabled={!isValid || creating || kpiLimitReached}
          onClick={handleCreate}
          title={kpiLimitReached ? quotaDisabledTooltip : undefined}
        >
          {t("team_kpi_view.builder_create", "Create KPI")}
        </button>
      </div>

    </aside>
      )}

      <Modal
        open={createChoiceOpen}
        title={t("team_kpi_view.builder_create_choice_title", "Create KPIs")}
        footer={null}
        centered
        destroyOnClose
        width={400}
        onCancel={() => setCreateChoiceOpen(false)}
        className={styles.createChoiceModal}
      >
        <p className={styles.createChoiceText}>
          {t("team_kpi_view.builder_create_choice_text", {
            count: selectedTopicCount,
            defaultValue:
              "You selected {{count}} topics. Create {{count}} separate KPIs, or combine them into one KPI?",
          })}
        </p>
        <div className={styles.createChoiceActions}>
          <button
            type="button"
            className={styles.createChoiceBtnSecondary}
            disabled={creating}
            onClick={() => submitCreate("separate")}
          >
            {t("team_kpi_view.builder_create_separate", {
              count: selectedTopicCount,
              defaultValue: "Create {{count}} separate KPIs",
            })}
          </button>
          <button
            type="button"
            className={styles.createChoiceBtnPrimary}
            disabled={creating}
            onClick={() => submitCreate("combined")}
          >
            {t("team_kpi_view.builder_create_combined", "Combine into one KPI")}
          </button>
        </div>
      </Modal>
    </>
  );
}
